import { Component, Inject, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { AssetManagement } from '@aptsi-types';
import { KeycloakService } from 'keycloak-angular';

import { KeycloakStateService } from 'src/app/core/service/utility.service';
import { SuccessDialogComponent } from 'src/app/shared/dialogs/success-dialog/success-dialog.component';
import { AssetsViewService } from '../../shared/services/assets-view.service';

export interface SystemAssetManualUpdateDialogData {
  /** Preferred property passed by the component opening this dialog. */
  assetId?: string;
  /** Backward-compatible alias for callers that use the service property name. */
  systemAssetId?: string;
}

@Component({
  selector: 'app-system-asset-manual-update',
  templateUrl: './system-asset-manual-update.component.html',
  styleUrls: ['./system-asset-manual-update.component.scss'],
})
export class SystemAssetManualUpdateComponent implements OnInit {
  form!: FormGroup;

  readonly systemAssetId: string;

  loading = false;
  saving = false;
  assetLoaded = false;
  pageError = '';

  importedBy = '';
  importedByKeycloakId = '';

  readonly statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'maintenance', label: 'Maintenance' },
    { value: 'retired', label: 'Retired' },
  ];

  readonly lifecycleOptions = [
    { value: 'Development', label: 'Development' },
    { value: 'Testing', label: 'Testing' },
    { value: 'Staging', label: 'Staging' },
    { value: 'Production', label: 'Production' },
    { value: 'Retired', label: 'Retired' },
  ];

  readonly classificationOptions = [
    { value: 'PUBLIC', label: 'Public' },
    { value: 'INTERNAL', label: 'Internal' },
    { value: 'CONFIDENTIAL', label: 'Confidential' },
    { value: 'RESTRICTED', label: 'Restricted' },
  ];

  constructor(
    @Inject(MAT_DIALOG_DATA)
    private readonly data: SystemAssetManualUpdateDialogData,
    private readonly dialogRef: MatDialogRef<SystemAssetManualUpdateComponent>,
    private readonly dialog: MatDialog,
    private readonly fb: FormBuilder,
    private readonly assetsViewService: AssetsViewService,
    private readonly keycloakStateService: KeycloakStateService,
    private readonly keycloakService: KeycloakService,
  ) {
    this.systemAssetId = String(data?.assetId ?? data?.systemAssetId ?? '').trim();
  }

  ngOnInit(): void {
    this.buildForm();
    this.importedBy = this.getImportedByFromKeycloak();
    this.importedByKeycloakId = this.getKeycloakUserId();

    if (!this.systemAssetId) {
      this.pageError = 'System Asset ID is missing. Unable to load this asset.';
      return;
    }

    this.loadManualSystemAsset();
  }

  get controls(): { [key: string]: AbstractControl } {
    return this.form.controls;
  }

  private buildForm(): void {
    this.form = this.fb.group({
      systemAssetName: [{ value: '', disabled: true }, [Validators.required, Validators.maxLength(120)]],
      systemAssetDescription: ['', [Validators.required, Validators.maxLength(1000)]],
      systemAssetOwner: ['', [Validators.required, Validators.maxLength(120)]],
      systemAssetStatus: ['active', Validators.required],
      systemAssetEndpoint: ['', [Validators.maxLength(2048), Validators.pattern(/^$|^https?:\/\/.+/i)]],
      systemAssetLifecycleStatus: ['Production', Validators.required],
      systemAssetBusinessValue: [5, [Validators.required, Validators.min(1), Validators.max(10)]],
      dataClassification: ['CONFIDENTIAL', Validators.required],
    });
  }

  loadManualSystemAsset(): void {
    if (this.loading || this.saving) {
      return;
    }

    this.loading = true;
    this.assetLoaded = false;
    this.pageError = '';

    this.assetsViewService.getManualSystemAsset(this.systemAssetId).subscribe({
      next: (response: AssetManagement) => {
        this.loading = false;

        if (!response) {
          this.pageError = 'No details were found for this system asset.';
          return;
        }

        const asset = response as any;
        this.form.patchValue({
          systemAssetName: asset.systemAssetName ?? '',
          systemAssetDescription: asset.systemAssetDescription ?? '',
          systemAssetOwner: asset.systemAssetOwner ?? '',
          systemAssetStatus: String(asset.systemAssetStatus ?? 'active').toLowerCase(),
          systemAssetEndpoint: asset.systemAssetEndpoint ?? '',
          systemAssetLifecycleStatus: asset.systemAssetLifecycleStatus ?? 'Production',
          systemAssetBusinessValue: this.toBusinessValue(asset.systemAssetBusinessValue),
          dataClassification: String(asset.dataClassification ?? 'CONFIDENTIAL').toUpperCase(),
        });

        this.assetLoaded = true;
      },
      error: (error: unknown) => {
        this.loading = false;
        this.pageError = this.extractMessage(error) || 'Unable to load the system asset details.';
      },
    });
  }

  save(): void {
    if (this.saving || this.loading || !this.assetLoaded) {
      return;
    }

    this.form.markAllAsTouched();
    if (this.form.invalid) {
      this.pageError = 'Review the highlighted fields and correct the validation errors.';
      return;
    }

    if (!this.importedByKeycloakId) {
      this.pageError = 'Unable to identify the signed-in user. Please sign in again and retry.';
      return;
    }

    this.pageError = '';
    this.saving = true;

    const value = this.form.getRawValue();
    this.assetsViewService.updateManualSystemAsset(
      this.systemAssetId,
      String(value.systemAssetDescription).trim(),
      String(value.systemAssetOwner).trim(),
      String(value.systemAssetStatus),
      String(value.systemAssetEndpoint ?? '').trim(),
      String(value.systemAssetLifecycleStatus),
      String(value.systemAssetBusinessValue),
      String(value.dataClassification),
      this.importedBy,
      this.importedByKeycloakId,
    ).subscribe({
      next: response => this.onSaveSuccess(response),
      error: error => this.onSaveError(error),
    });
  }

  cancel(): void {
    if (!this.saving) {
      this.dialogRef.close(false);
    }
  }

  isInvalid(controlName: string): boolean {
    const control = this.form.get(controlName);
    return Boolean(control && control.invalid && (control.touched || control.dirty));
  }

  private onSaveSuccess(response: AssetManagement): void {
    this.saving = false;

    const successRef = this.dialog.open(SuccessDialogComponent, {
      width: '380px',
      data: {
        isCreate: false,
        title: 'System Asset',
        message: this.extractMessage(response) || 'System asset updated successfully.',
      },
    });

    successRef.afterClosed().subscribe(() => {
      this.dialogRef.close({ updated: true, response });
    });
  }

  private onSaveError(error: unknown): void {
    this.saving = false;
    this.pageError = this.extractMessage(error) || 'Unable to update the system asset.';
  }

  private getImportedByFromKeycloak(): string {
    try {
      const keycloak = this.keycloakService.getKeycloakInstance();
      const token = (keycloak?.tokenParsed || {}) as Record<string, unknown>;
      const userLabel = token['preferred_username'] || token['name'] || token['email'];
      return userLabel ? String(userLabel) : '';
    } catch {
      return '';
    }
  }

  private getKeycloakUserId(): string {
    try {
      const stateUserId = this.keycloakStateService.getUserId();
      if (stateUserId) {
        return String(stateUserId);
      }

      const keycloak = this.keycloakService.getKeycloakInstance();
      const token = (keycloak?.tokenParsed || {}) as Record<string, unknown>;
      return token['sub'] ? String(token['sub']) : '';
    } catch {
      return '';
    }
  }

  private toBusinessValue(value: unknown): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed >= 1 && parsed <= 10 ? parsed : 5;
  }

  private extractMessage(responseOrError: any): string {
    if (!responseOrError) {
      return '';
    }

    if (typeof responseOrError === 'string') {
      return responseOrError.trim();
    }

    const candidates = [
      responseOrError.message,
      responseOrError.error?.message,
      responseOrError.error?.errorMessage,
      responseOrError.error?.details,
      responseOrError.errorMessage,
    ];

    const message = candidates.find(
      candidate => typeof candidate === 'string' && candidate.trim(),
    );
    return message ? String(message).trim() : '';
  }
}
